import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { agentsService, channelsService, teamsService, labelsService } from '../services/api';
import { FunnelIcon, ArrowDownTrayIcon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

// Componente de Multi-Select com Dropdown
const MultiSelect = ({ label, options, selected, onChange, placeholder, disabled, renderOption }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleSelectAll = () => {
    if (selected.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map(opt => opt.value));
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full min-w-[180px] px-3 py-2 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400'}`}
      >
        <span className={`truncate ${selected.length === 0 ? 'text-gray-500' : 'text-gray-900'}`}>
          {selected.length === 0 ? placeholder : `${selected.length} selecionado(s)`}
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Selecionar todos */}
          <div className="p-2 border-b border-gray-100 bg-gray-50">
            <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-1 rounded">
              <input
                type="checkbox"
                checked={selected.length === options.length && options.length > 0}
                onChange={handleSelectAll}
                className="rounded text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">
                {selected.length === options.length ? 'Desmarcar todos' : 'Selecionar todos'}
              </span>
            </label>
          </div>

          {/* Lista de opções */}
          <div className="overflow-y-auto max-h-48">
            {options.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-primary-50 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option.value)}
                  onChange={() => handleToggle(option.value)}
                  className="rounded text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700 truncate">
                  {renderOption ? renderOption(option) : option.label}
                </span>
              </label>
            ))}
          </div>

          {/* Limpar seleção */}
          {selected.length > 0 && (
            <div className="p-2 border-t border-gray-100 bg-gray-50">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Limpar seleção
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FilterBar = ({ onFilterChange, showConversationType = false, showTeams = false, showLabels = false }) => {
  const [startDate, setStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState(new Date());
  const [agents, setAgents] = useState([]);
  const [channels, setChannels] = useState([]);
  const [teams, setTeams] = useState([]);
  const [labels, setLabels] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [conversationType, setConversationType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  // Carregar opções de filtros
  useEffect(() => {
    const loadFilters = async () => {
      try {
        setLoading(true);
        const promises = [
          agentsService.getAll(),
          channelsService.getAll(),
        ];

        if (showTeams) {
          promises.push(teamsService.getAll());
        }
        if (showLabels) {
          promises.push(labelsService.getAll());
        }

        const results = await Promise.all(promises);
        setAgents(results[0].data.data || []);
        setChannels(results[1].data.data || []);

        let resultIndex = 2;
        if (showTeams && results[resultIndex]) {
          setTeams(results[resultIndex].data.data || []);
          resultIndex++;
        }
        if (showLabels && results[resultIndex]) {
          setLabels(results[resultIndex].data.data || []);
        }
      } catch (error) {
        console.error('Error loading filters:', error);
      } finally {
        setLoading(false);
      }
    };
    loadFilters();
  }, [showTeams, showLabels]);

  // Detectar mudanças nos filtros
  useEffect(() => {
    setHasChanges(true);
  }, [startDate, endDate, selectedAgents, selectedChannels, selectedTeams, selectedLabels, conversationType]);

  // Aplicar filtros automaticamente na primeira carga
  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => {
    const filters = {
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      agentId: selectedAgents.length > 0 ? selectedAgents.join(',') : undefined,
      inboxId: selectedChannels.length > 0 ? selectedChannels.join(',') : undefined,
      teamId: selectedTeams.length > 0 ? selectedTeams.join(',') : undefined,
      labelIds: selectedLabels.length > 0 ? selectedLabels.join(',') : undefined,
      conversationType: conversationType !== 'all' ? conversationType : undefined,
    };
    onFilterChange(filters);
    setHasChanges(false);
  };

  const resetFilters = () => {
    setStartDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
    setEndDate(new Date());
    setSelectedAgents([]);
    setSelectedChannels([]);
    setSelectedTeams([]);
    setSelectedLabels([]);
    setConversationType('all');

    // Aplicar filtros limpos
    setTimeout(() => {
      const filters = {
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        endDate: new Date().toISOString(),
      };
      onFilterChange(filters);
      setHasChanges(false);
    }, 0);
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate.toISOString());
      if (endDate) params.append('endDate', endDate.toISOString());
      if (selectedAgents.length > 0) params.append('agentId', selectedAgents.join(','));
      if (selectedChannels.length > 0) params.append('inboxId', selectedChannels.join(','));

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003';
      const url = `${API_URL}/api/metrics/export/excel?${params.toString()}`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Erro ao exportar para Excel. Tente novamente.');
    }
  };

  // Preparar opções para os multi-selects
  const agentOptions = agents.map(agent => ({ value: agent.id.toString(), label: agent.name }));
  const channelOptions = channels.map(channel => ({ value: channel.id.toString(), label: channel.name, type: channel.channel_type }));
  const teamOptions = teams.map(team => ({ value: team.id.toString(), label: team.name }));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
      {/* Linha principal de filtros */}
      <div className="flex flex-wrap gap-4 items-end">
        {/* Data Início */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Data Início
          </label>
          <DatePicker
            selected={startDate}
            onChange={setStartDate}
            dateFormat="dd/MM/yyyy"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            maxDate={endDate}
          />
        </div>

        {/* Data Fim */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Data Fim
          </label>
          <DatePicker
            selected={endDate}
            onChange={setEndDate}
            dateFormat="dd/MM/yyyy"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            minDate={startDate}
            maxDate={new Date()}
          />
        </div>

        {/* Multi-Select Agentes */}
        <MultiSelect
          label="Agentes"
          options={agentOptions}
          selected={selectedAgents}
          onChange={setSelectedAgents}
          placeholder="Todos os agentes"
          disabled={loading}
        />

        {/* Multi-Select Canais */}
        <MultiSelect
          label="Canais"
          options={channelOptions}
          selected={selectedChannels}
          onChange={setSelectedChannels}
          placeholder="Todos os canais"
          disabled={loading}
          renderOption={(opt) => `${opt.label} (${opt.type})`}
        />

        {/* Tipo de Conversa (condicional) */}
        {showConversationType && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Conversa
            </label>
            <select
              value={conversationType}
              onChange={(e) => setConversationType(e.target.value)}
              className="w-full min-w-[160px] px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">Todas</option>
              <option value="receptive">Receptivas</option>
              <option value="active">Ativas</option>
            </select>
          </div>
        )}

        {/* Multi-Select Equipes (condicional) */}
        {showTeams && (
          <MultiSelect
            label="Equipes"
            options={teamOptions}
            selected={selectedTeams}
            onChange={setSelectedTeams}
            placeholder="Todas as equipes"
            disabled={loading}
          />
        )}

        {/* Botões de Ação */}
        <div className="flex gap-2 ml-auto">
          {/* Botão Filtrar */}
          <button
            onClick={applyFilters}
            className={`px-5 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
              hasChanges
                ? 'bg-primary-500 text-white hover:bg-primary-600 shadow-md'
                : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
            }`}
          >
            <FunnelIcon className="w-5 h-5" />
            Filtrar
          </button>

          {/* Botão Exportar Excel */}
          <button
            onClick={handleExportExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            title="Exportar para Excel"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
            <span className="hidden sm:inline">Exportar</span>
          </button>

          {/* Botão Limpar */}
          <button
            onClick={resetFilters}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
            title="Limpar filtros"
          >
            <XMarkIcon className="w-5 h-5" />
            <span className="hidden sm:inline">Limpar</span>
          </button>
        </div>
      </div>

      {/* Etiquetas (multi-select visual, condicional) */}
      {showLabels && labels.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Etiquetas
          </label>
          <div className="flex flex-wrap gap-2">
            {labels.map((label) => (
              <button
                key={label.id}
                onClick={() => {
                  const labelId = label.id.toString();
                  if (selectedLabels.includes(labelId)) {
                    setSelectedLabels(selectedLabels.filter(id => id !== labelId));
                  } else {
                    setSelectedLabels([...selectedLabels, labelId]);
                  }
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  selectedLabels.includes(label.id.toString())
                    ? 'ring-2 ring-offset-2 ring-primary-500 scale-105'
                    : 'opacity-70 hover:opacity-100 hover:scale-105'
                }`}
                style={{
                  backgroundColor: label.color || '#6b7280',
                  color: '#ffffff'
                }}
              >
                {label.title}
              </button>
            ))}
          </div>
          {selectedLabels.length > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              {selectedLabels.length} etiqueta(s) selecionada(s)
            </p>
          )}
        </div>
      )}

      {/* Indicador de filtros ativos */}
      {(selectedAgents.length > 0 || selectedChannels.length > 0 || selectedTeams.length > 0 || selectedLabels.length > 0 || conversationType !== 'all') && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500">Filtros ativos:</span>
          {selectedAgents.length > 0 && (
            <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
              {selectedAgents.length} agente(s)
            </span>
          )}
          {selectedChannels.length > 0 && (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
              {selectedChannels.length} canal(is)
            </span>
          )}
          {selectedTeams.length > 0 && (
            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
              {selectedTeams.length} equipe(s)
            </span>
          )}
          {selectedLabels.length > 0 && (
            <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
              {selectedLabels.length} etiqueta(s)
            </span>
          )}
          {conversationType !== 'all' && (
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
              {conversationType === 'receptive' ? 'Receptivas' : 'Ativas'}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterBar;
